'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { Check, Copy } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/atoms/buttons/Button';
import { SUGAR_HIGH_STYLE } from '@/lib/syntax-highlight';
import {
  renderHighlightedCode,
  renderHighlightedMarkup,
} from '@/lib/syntax-highlight-react';
import { cn } from '@/lib/utils';

const codeSnippetVariants = cva('overflow-hidden rounded-xl bg-background', {
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-base',
      lg: 'text-lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const lineNumberVariants = cva(
  'select-none font-mono font-bold leading-normal text-secondary-foreground',
  {
    variants: {
      size: {
        sm: 'text-xs',
        md: 'text-base',
        lg: 'text-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export interface CodeSnippetProps
  extends VariantProps<typeof codeSnippetVariants> {
  /** Code content to display */
  code: string;
  /** Programming language label shown in the top-right corner */
  language?: string;
  /** File name shown in the file bar */
  fileName?: string;
  /** Whether to show the file bar */
  showHeader?: boolean;
  /** Whether to show line numbers */
  showLineNumbers?: boolean;
  /** Starting line number */
  startLineNumber?: number;
  /** Whether to show a copy-to-clipboard button */
  showCopyButton?: boolean;
  /** Additional class name */
  className?: string;
  /** Responsive breakpoint for compact mode */
  breakpoint?: 'desktop' | 'mobile';
  /** One-based line numbers to emphasize */
  highlightedLines?: readonly number[];
  /** Pre-highlighted sugar-high markup */
  highlightedCode?: string;
  /** Additional controls shown in the file bar */
  headerActions?: React.ReactNode;
  /** Optional tabs or selector shown above the file bar */
  toolbar?: React.ReactNode;
}

interface LineNumbersProps {
  count: number;
  startLine: number;
  size?: 'sm' | 'md' | 'lg' | null;
}

interface CopyFeedbackState {
  copied: boolean;
  resetKey: number;
}

function copyFeedbackReducer(
  state: CopyFeedbackState,
  copied: boolean,
): CopyFeedbackState {
  if (!copied) return state.copied ? { ...state, copied: false } : state;
  return { copied: true, resetKey: state.resetKey + 1 };
}

/** Fixed icon slot: both icons stacked, opacity-only 120 ms crossfade, instant under reduced motion. */
const COPY_ICON_SLOT_CLASS =
  'relative inline-grid size-4 shrink-0 *:col-start-1 *:row-start-1 *:size-4 *:transition-opacity *:duration-(--duration-fast) *:ease-standard motion-reduce:*:transition-none';

function getStartLineNumber(startLineNumber: number): number {
  return Number.isFinite(startLineNumber) ? Math.floor(startLineNumber) : 1;
}

function LineNumbers({ count, startLine, size }: LineNumbersProps) {
  return (
    <div aria-hidden="true" className={cn(lineNumberVariants({ size }))}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="block mb-px last:mb-0">
          {startLine + i}
        </span>
      ))}
    </div>
  );
}

const CodeSnippet = React.forwardRef<HTMLElement, CodeSnippetProps>(
  (
    {
      code,
      language,
      fileName,
      showHeader = true,
      showLineNumbers = true,
      startLineNumber = 1,
      showCopyButton = false,
      size,
      className,
      breakpoint = 'desktop',
      highlightedLines,
      highlightedCode,
      headerActions,
      toolbar,
    },
    ref,
  ) => {
    const highlightedContent = React.useMemo(
      () =>
        highlightedCode
          ? renderHighlightedMarkup(highlightedCode)
          : renderHighlightedCode(code, highlightedLines),
      [code, highlightedCode, highlightedLines],
    );
    const lineCount = React.useMemo(() => code.split('\n').length, [code]);
    const safeStartLineNumber = getStartLineNumber(startLineNumber);

    const [copyFeedback, setCopyFeedback] = React.useReducer(
      copyFeedbackReducer,
      { copied: false, resetKey: 0 },
    );
    const { copied } = copyFeedback;
    const rootRef = React.useRef<HTMLElement>(null);
    const copyRequestRef = React.useRef(0);

    // Merge forwarded ref with local ref
    React.useImperativeHandle(ref, () => rootRef.current as HTMLElement);

    React.useEffect(() => {
      if (!copyFeedback.copied) return;

      const copyResetTimer = setTimeout(() => {
        setCopyFeedback(false);
      }, 2000);

      return () => {
        clearTimeout(copyResetTimer);
      };
    }, [copyFeedback]);

    // A code change (or unmount) invalidates any pending copy and clears the acknowledgement.
    // biome-ignore lint/correctness/useExhaustiveDependencies: `code` is the intentional trigger.
    React.useEffect(() => {
      setCopyFeedback(false);
      return () => {
        copyRequestRef.current += 1;
      };
    }, [code]);

    const handleCopy = React.useCallback(async () => {
      const request = ++copyRequestRef.current;
      try {
        const win = rootRef.current?.ownerDocument.defaultView;
        if (!win) return;
        await win.navigator.clipboard.writeText(code);
        if (request !== copyRequestRef.current) return;
        setCopyFeedback(true);
      } catch (err) {
        console.error('Failed to copy code:', err);
        // A failed attempt must never leave an earlier acknowledgement visible.
        if (request === copyRequestRef.current) setCopyFeedback(false);
      }
    }, [code]);

    const contentPadding = breakpoint === 'mobile' ? 'p-4' : 'p-6';
    const hasHeader =
      showHeader &&
      Boolean(fileName || language || showCopyButton || headerActions);

    return (
      <section
        ref={rootRef}
        data-slot="code-snippet"
        aria-label="Code snippet"
        className={cn(
          codeSnippetVariants({ size }),
          'relative flex flex-col border border-border',
          className,
        )}
        style={SUGAR_HIGH_STYLE}
      >
        {toolbar && (
          <div
            data-slot="code-snippet-toolbar"
            className="border-b border-border"
          >
            {toolbar}
          </div>
        )}

        {hasHeader && (
          <div
            data-slot="code-snippet-header"
            className="flex min-h-10 items-center justify-between gap-3 border-b border-border px-4"
          >
            <span className="truncate text-sm font-medium text-foreground">
              {fileName ?? language}
            </span>
            <div className="flex items-center gap-1">
              {headerActions}
              {showCopyButton && (
                <Button
                  appearance="ghost"
                  variant="secondary"
                  size="icon-sm"
                  onClick={handleCopy}
                  aria-label={
                    copied ? 'Copied to clipboard' : 'Copy code to clipboard'
                  }
                  className="text-secondary-foreground hover:text-foreground"
                >
                  <span
                    data-slot="code-snippet-copy-icon"
                    aria-hidden="true"
                    className={COPY_ICON_SLOT_CLASS}
                  >
                    <Copy className={copied ? 'opacity-0' : 'opacity-100'} />
                    <Check
                      className={cn(
                        'text-success',
                        copied ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </span>
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="flex min-w-0 items-stretch">
          {showLineNumbers && (
            <div
              className={cn(
                'shrink-0 bg-muted border-e border-border',
                contentPadding,
              )}
            >
              <LineNumbers
                count={lineCount}
                startLine={safeStartLineNumber}
                size={size}
              />
            </div>
          )}

          <section
            className={cn(
              'grow min-w-0 overflow-x-auto [overflow-wrap:normal] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
              contentPadding,
            )}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: Focusable scroll region satisfies axe scrollable-region-focusable.
            tabIndex={0}
            aria-label="Code content"
          >
            <pre
              className={cn(
                'font-mono font-medium leading-normal [overflow-wrap:normal] !bg-transparent !p-0 !m-0 !border-0 !rounded-none',
                '[&_.sh__line]:block [&_.sh__line]:leading-normal [&_.sh__line]:mb-px [&_.sh__line:last-child]:mb-0',
                '[&_.sh__line[data-highlighted=true]]:-mx-2 [&_.sh__line[data-highlighted=true]]:rounded-sm [&_.sh__line[data-highlighted=true]]:bg-primary/10 [&_.sh__line[data-highlighted=true]]:px-2',
              )}
            >
              {highlightedContent}
            </pre>
          </section>
        </div>
      </section>
    );
  },
);

CodeSnippet.displayName = 'CodeSnippet';

export {
  COPY_ICON_SLOT_CLASS,
  CodeSnippet,
  codeSnippetVariants,
  lineNumberVariants,
};
